import React, { useState, useEffect } from "react";
import axios from "axios";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import CustomerAdd from "./CustomerAdd";
import CustomerEdit from "./CustomerEdit";
import CollectPaymentDialog from "./CollectPaymentDialog";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { exportCustomerReportToPDF } from "../../utils/exportToPDF";

import { formatJalaliDateTime } from "../utils/dateFormat";
const CustomerIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openCollectPayment, setOpenCollectPayment] = useState(false);
  const [customerForPayment, setCustomerForPayment] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadReport = async () => {
    if (!token) return;
    setDownloadLoading(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || "";
      const { data } = await axios.get(`${base}/shop/customer-report/`, {
        headers: { Authorization: `Token ${token}` },
      });
      exportCustomerReportToPDF(data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || t("customer.reportFailed", "Failed to load report"));
    } finally {
      setDownloadLoading(false);
    }
  };

  // Fetch customers with search filtering and pagination.
  const apiUrl = rowsPerPage === "all" 
    ? `shop/customers/?search=${searchTerm}&page_size=10000`
    : `shop/customers/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`;
  
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["customer", searchTerm, page, rowsPerPage],
    apiUrl,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/customers", token);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    setRowsPerPage(value === "all" ? "all" : Number(value));
    setPage(1);
  };

  const customers = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : customers.length;
  const totalPages = rowsPerPage === "all" ? 1 : Math.ceil(totalCount / rowsPerPage);

  if (isError) {
    return <ErrorPage message={t("customer.failedToLoadCustomers")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("customer.customerList")}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={downloadLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={downloadLoading}
          >
            {downloadLoading ? t("customer.downloading", "Downloading...") : t("customer.downloadReport", "Download report")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAdd(true)}
          >
            {t("customer.addNewCustomer")}
          </Button>
        </Box>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("customer.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>
      {/* Search Field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("customer.searchCustomers")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Customers Table */}
      <TableContainer
        component={Paper}
        sx={{ mt: 2, maxHeight: 540, overflowY: "auto" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="customer table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("customer.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.name")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.email")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.contact")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.address")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.openingBalance", "Opening balance")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.amountOwed", "Amount owed")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.shop")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("customer.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Name",
                      "Email",
                      "Contact",
                      "Address",
                      "Opening",
                      "Owed",
                      "Branch",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : customers.map((customer) => {
                  const openingStr =
                    customer.opening_balance != null &&
                    Number(customer.opening_balance) !== 0
                      ? `${Number(customer.opening_balance).toLocaleString()} ${
                          customer.opening_balance_currency_details?.title || ""
                        }`.trim()
                      : "--";
                  const owed = customer.total_owed_by_currency || {};
                  const owedStr =
                    Object.keys(owed).length > 0
                      ? Object.entries(owed)
                          .map(
                            ([curr, amt]) =>
                              `${Number(amt).toLocaleString()} ${curr}`
                          )
                          .join(" / ")
                      : "--";
                  return (
                    <TableRow key={customer.id} hover>
                      <TableCell>
                        {rowsPerPage === "all" 
                          ? customers.indexOf(customer) + 1
                          : (page - 1) * rowsPerPage + customers.indexOf(customer) + 1}
                      </TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.contact}</TableCell>
                      <TableCell>{customer.address}</TableCell>
                      <TableCell>{openingStr}</TableCell>
                      <TableCell>{owedStr}</TableCell>
                      <TableCell>
                        {customer.shop_details?.name || "--"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => {
                            setCustomerForPayment(customer);
                            setOpenCollectPayment(true);
                          }}
                        >
                          {t("customer.collectPayment", "Collect payment")}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => {
                            setEditData(customer);
                            setOpenEdit(true);
                          }}
                        >
                          {t("customer.edit")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(customer.id)}
                        >
                          {t("customer.delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("customer.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== "all" && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
            sx={{ ml: 2 }}
          />
        )}
      </Grid>

      {openAdd && (
        <CustomerAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <CustomerEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      {openCollectPayment && (
        <CollectPaymentDialog
          open={openCollectPayment}
          onClose={() => {
            setOpenCollectPayment(false);
            setCustomerForPayment(null);
          }}
          customer={customerForPayment}
          token={token}
          onSuccess={refetch}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
};

export default CustomerIndex;
